import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  Nullable,
  ParsedTournament,
  Tournament,
  TournamentStatus,
  TournamentType,
  UserWithToken,
  WithData,
} from '@ygoargentina/sdk/api/api.types';
import type { DuplicateMatchReason } from './limitlessPending';

export type ChallongePendingStatus = 'PENDING' | 'IMPORTED' | 'DISMISSED';

/**
 * Misma forma que la de Limitless y TopDeck, para que el front use el mismo
 * aviso de duplicados. Acá `matchedPlayers`/`pendingPlayers` vienen siempre en
 * 0: la detección de Challonge no cruza jugadores.
 */
export type ChallongeDuplicateCandidate = {
  tournament: {
    id: number;
    title: string;
    dateFrom: string | null;
    status: TournamentStatus;
  };
  matchedPlayers: number;
  pendingPlayers: number;
  coverage: number;
  nameSimilarity: number;
  reasons: DuplicateMatchReason[];
};

export type ChallongePendingTournament = {
  id: number;
  /** Slug de la URL: `https://challonge.com/<challongeId>`. */
  challongeId: string;
  challongeUsername: string;
  name: string;
  detectedFormat: number | null;
  startDate: string;
  participants: number;
  /** `tournament_type` de Challonge: swiss, single elimination, ... */
  tournamentType: string;
  status: ChallongePendingStatus;
  detectedAt: string;
  processedAt: string | null;
  organizer: { id: number; name: string };
  tournament: Tournament | null;
  possibleDuplicates: ChallongeDuplicateCandidate[];
};

/**
 * Los torneos de una organización de Challonge viven en
 * `<sub>.challonge.com/<slug>` y la API los nombra `<sub>-<slug>`. El slug no
 * lleva guiones (sólo letras, números y `_`) y el subdominio sí puede: por eso
 * se corta en el último.
 */
export const challongeTournamentUrl = (challongeId: string) => {
  const dash = challongeId.lastIndexOf('-');
  return dash > 0
    ? `https://${challongeId.slice(0, dash)}.challonge.com/${challongeId.slice(dash + 1)}`
    : `https://challonge.com/${challongeId}`;
};

const PENDING_FIELDS = gql`
  fragment ChallongePendingFields on ChallongePendingTournament {
    id
    challongeId
    challongeUsername
    name
    detectedFormat
    startDate
    participants
    tournamentType
    status
    detectedAt
    processedAt
    organizer {
      id
      name
    }
    # Los campos extra son los que pide getTournamentDisplayStatus.
    tournament {
      id
      title
      status
      dateFrom
      dateTo
      rounds
      currentRound
      tournamentType
      deletedAt
    }
    possibleDuplicates {
      tournament {
        id
        title
        dateFrom
        status
      }
      matchedPlayers
      pendingPlayers
      coverage
      nameSimilarity
      reasons
    }
  }
`;

export const getChallongePending = async (
  user: Nullable<UserWithToken>,
  status?: ChallongePendingStatus
) =>
  getGraphQLQueryClient(user).request<WithData<ChallongePendingTournament[]>>(
    gql`
      ${PENDING_FIELDS}
      query challongePendingTournaments($status: ChallongePendingStatus) {
        data: challongePendingTournaments(status: $status) {
          ...ChallongePendingFields
        }
      }
    `,
    { status: status ?? null }
  );

/**
 * Encola la detección en el box de workers y vuelve enseguida: los pendientes
 * aparecen cuando el job termina.
 */
export const detectChallongeTournaments = async (
  user: Nullable<UserWithToken>,
  days: number
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation detectChallongeTournaments($days: Int) {
        data: detectChallongeTournaments(days: $days)
      }
    `,
    { days }
  );

export const dismissChallongePending = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<ChallongePendingTournament>>(
    gql`
      ${PENDING_FIELDS}
      mutation dismissChallongePending($id: Int!) {
        data: dismissChallongePending(id: $id) {
          ...ChallongePendingFields
        }
      }
    `,
    { id }
  );

export const linkChallongePending = async (
  user: Nullable<UserWithToken>,
  id: number,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<ChallongePendingTournament>>(
    gql`
      ${PENDING_FIELDS}
      mutation linkChallongePending($id: Int!, $tournamentId: Int!) {
        data: linkChallongePending(id: $id, tournamentId: $tournamentId) {
          ...ChallongePendingFields
        }
      }
    `,
    { id, tournamentId }
  );

export const importChallongePending = async (
  user: Nullable<UserWithToken>,
  id: number,
  /** Torneo del calendario sobre el que importar. Sin esto se crea uno. */
  tournamentId?: number,
  /** Tipo del torneo nuevo; por defecto suizo. Se ignora con `tournamentId`. */
  tournamentType?: TournamentType
) =>
  getGraphQLQueryClient(user).request<
    WithData<{
      tournament: Tournament;
      pending: ChallongePendingTournament;
      importData: Nullable<ParsedTournament>;
    }>
  >(
    gql`
      ${PENDING_FIELDS}
      mutation importChallongePending(
        $id: Int!
        $tournamentId: Int
        $tournamentType: TournamentType
      ) {
        data: importChallongePending(
          id: $id
          tournamentId: $tournamentId
          tournamentType: $tournamentType
        ) {
          tournament {
            id
            title
            challongeId
          }
          pending {
            ...ChallongePendingFields
          }
          importData
        }
      }
    `,
    { id, tournamentId, tournamentType }
  );
